import { useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../shared/api/client";
import { AppButton } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import { IconCheck, IconError, IconHourglass, IconSpinner, IconWarning } from "../../../shared/components/icons";
import { getLead, submitKyc } from "../api";
import { useOfflineQueue, useOnlineStatus } from "../hooks";
import {
  enqueue,
  findLocalLeadByServerId,
  getLocalLead,
  newLocalId,
  normalisePhotos,
  upsertLocalLead,
  type LeadPhotos,
} from "../store";
import type { GeofenceState, Lead } from "../types";

/**
 * S4 — KYC submission (handoff TASK-002, s4-kyc-submission-*).
 * Read-only summary of the captured lead → POST /kyc/submissions with an
 * Idempotency-Key. Handles 422 geofence-block (save-draft offered) and 422
 * missing-fields (listed). Offline → queued variant.
 */

const REQUIRED: { key: keyof Lead; label: string }[] = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "phone", label: "Phone" },
  { key: "national_id", label: "NIDA" },
  { key: "ward", label: "Ward" },
  { key: "address", label: "Address" },
  { key: "gps_lat", label: "GPS" },
  { key: "gps_lng", label: "GPS" },
];

function missingFields(lead: Lead): string[] {
  const missing = new Set<string>();
  for (const { key, label } of REQUIRED) {
    const v = lead[key];
    if (v === null || v === undefined || v === "") missing.add(label);
  }
  return [...missing];
}

function gpsSummary(lead: Lead): string {
  if (lead.gps_lat === null || lead.gps_lng === null) return "Not captured";
  const state: GeofenceState = lead.geofence_state;
  const acc = lead.gps_accuracy_m !== null ? ` · ±${Math.round(lead.gps_accuracy_m)} m` : "";
  if (state === "in_range") return `In range${acc}`;
  if (state === "out_of_range") return `Out of range${acc}`;
  return `Captured${acc}`;
}

export function S4KycSubmission() {
  const { id = "" } = useParams();
  const isLocal = id.startsWith("local-");
  const localRecord = isLocal ? getLocalLead(id) : undefined;
  useFieldHeader("Submit KYC", "Confirm captured data");
  useFieldJourney({
    flow: "leads",
    step: 3,
    backTo: id ? `/field/leads/${id}/duplicates` : "/field/leads",
  });

  if (isLocal && localRecord?.serverId !== undefined) {
    return <Navigate to={`/field/leads/${localRecord.serverId}/kyc`} replace />;
  }

  if (isLocal) {
    // Lead not yet created on the server — KYC cannot start until sync.
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconHourglass size={40} />
        </span>
        <h2>Waiting for sync</h2>
        <p>This lead is saved on the device. KYC submission unlocks once the lead has synced.</p>
      </div>
    );
  }

  return <S4Online leadId={Number(id)} />;
}

function S4Online({ leadId }: { leadId: number }) {
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const { refresh: refreshQueue } = useOfflineQueue(online);
  useFieldHeader("Submit KYC", "Confirm captured data");
  useFieldJourney({
    flow: "leads",
    step: 3,
    backTo: `/field/leads/${leadId}/duplicates`,
  });

  // Stable per-mount idempotency key — safe to retry the same submission.
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [queued, setQueued] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  const query = useQuery({
    queryKey: ["lead", leadId],
    queryFn: () => getLead(leadId),
    retry: 1,
    staleTime: 15_000,
  });

  const local = findLocalLeadByServerId(leadId);

  const mutation = useMutation({
    mutationFn: () => submitKyc(leadId, idempotencyKey),
    onSuccess: () => {
      if (local) upsertLocalLead({ ...local, status: "kyc-submitted" });
    },
  });

  const missing = useMemo(() => (query.data ? missingFields(query.data.lead) : []), [query.data]);

  const handleSubmit = () => {
    if (!online) {
      enqueueKyc();
      return;
    }
    mutation.mutate();
  };

  const enqueueKyc = () => {
    enqueue({
      queueId: crypto.randomUUID(),
      kind: "kyc-submission",
      leadRef: String(leadId),
      idempotencyKey,
      createdAt: new Date().toISOString(),
    });
    refreshQueue();
    setQueued(true);
  };

  const handleSaveDraft = () => {
    // Geofence-blocked: keep a device draft; submission stays blocked.
    const lead = query.data?.lead;
    upsertLocalLead({
      localId: local?.localId ?? newLocalId(),
      serverId: leadId,
      name: lead ? `${lead.first_name} ${lead.last_name}` : `Lead ${leadId}`,
      phone: lead?.phone,
      ward: lead?.ward ?? undefined,
      status: "draft",
      createdAt: local?.createdAt ?? new Date().toISOString(),
      formDraft: lead
        ? {
            first_name: lead.first_name,
            last_name: lead.last_name,
            phone: lead.phone,
            national_id: lead.national_id ?? undefined,
            ward: lead.ward ?? undefined,
            address: lead.address ?? undefined,
            gps_lat: lead.gps_lat ?? undefined,
            gps_lng: lead.gps_lng ?? undefined,
            gps_accuracy_m: lead.gps_accuracy_m ?? undefined,
            geofence_state: lead.geofence_state,
          }
        : undefined,
      photos: local?.photos,
      duplicateDecision: local?.duplicateDecision,
    });
    refreshQueue();
    setDraftSaved(true);
  };

  // ── Loading / error fetching the lead ──────────────────────────────────────
  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Loading captured data…</h2>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Could not load lead</h2>
        <p>{query.error instanceof Error ? query.error.message : "Something went wrong"}</p>
        <AppButton variant="secondary" type="button" onClick={() => void query.refetch()}>
          Retry
        </AppButton>
      </div>
    );
  }

  const lead = query.data.lead;

  // ── Queued (offline / saved for later) ─────────────────────────────────────
  if (queued) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconHourglass size={40} />
        </span>
        <h2>KYC queued for sync</h2>
        <p>The submission will be sent automatically when back online.</p>
        <AppButton variant="secondary" type="button" onClick={() => navigate("/field/leads")}>
          Back to Leads
        </AppButton>
      </div>
    );
  }

  // ── Success ────────────────────────────────────────────────────────────────
  if (mutation.isSuccess) {
    return (
      <div className="pg-state">
        <span className="state-icon success">
          <IconCheck size={40} />
        </span>
        <h2>KYC submitted for review</h2>
        <p>
          Reference #{mutation.data.submission.id}.
          <br />
          Typically reviewed within 1 working day.
          <br />
          You will be notified of the decision.
        </p>
        <AppButton type="button" onClick={() => navigate("/field/leads")}>
          Back to Leads
        </AppButton>
        <AppButton
          variant="secondary"
          type="button"
          onClick={() => navigate("/customers")}
        >
          Continue to next — Contract
        </AppButton>
      </div>
    );
  }

  // ── Submitting ─────────────────────────────────────────────────────────────
  if (mutation.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Submitting KYC…</h2>
        <p>Confirm &amp; submit in progress</p>
      </div>
    );
  }

  // ── 422 responses ──────────────────────────────────────────────────────────
  const apiErr = mutation.error instanceof ApiError ? mutation.error : null;
  const details = apiErr?.details ?? {};
  const serverMissing = Array.isArray(details.missing_fields)
    ? (details.missing_fields as string[])
    : null;
  const geofenceBlocked = details.geofence_state === "out_of_range";

  if (apiErr && apiErr.status === 422 && geofenceBlocked) {
    return (
      <>
        <div className="pg-state">
          <span className="state-icon warning">
            <IconWarning size={40} />
          </span>
          <h2>Submission blocked</h2>
          <p>
            KYC submission blocked: lead GPS is out of range.
            <br />
            Save a draft and re-capture GPS inside the service fence.
          </p>
        </div>
        <div className="pg-actions">
          {draftSaved ? (
            <div className="pg-draft-note" role="status">
              <IconCheck size={14} /> Draft saved on device
            </div>
          ) : (
            <AppButton variant="secondary" type="button" onClick={handleSaveDraft}>
              Save draft
            </AppButton>
          )}
          <AppButton variant="secondary" type="button" onClick={() => navigate("/field/leads")}>
            Back to Leads
          </AppButton>
        </div>
      </>
    );
  }

  if (apiErr && apiErr.status === 422 && serverMissing) {
    return (
      <>
        <SummaryRows lead={lead} photosText={photosText(lead, normalisePhotos(local?.photos))} decisionText={decisionText(local?.duplicateDecision?.decision)} />
        <div className="pg-inline-error" role="alert">
          <IconWarning size={14} />
          <span>
            {serverMissing.length} item{serverMissing.length === 1 ? "" : "s"} incomplete — KYC
            cannot be submitted yet
            <ul>
              {serverMissing.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </span>
        </div>
        <div className="pg-actions">
          <AppButton
            variant="secondary"
            type="button"
            onClick={() =>
              navigate(local?.formDraft ? `/field/leads/new?draft=${local.localId}` : "/field/leads/new")
            }
          >
            Return to Form
          </AppButton>
        </div>
      </>
    );
  }

  // ── Generic submit failure (retry / save offline) ──────────────────────────
  if (mutation.isError) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Submission failed</h2>
        <p>
          {mutation.error instanceof Error ? mutation.error.message : "Server unreachable."}
          <br />
          Your data is safe on this device.
        </p>
        <AppButton variant="secondary" type="button" onClick={() => mutation.mutate()}>
          Retry
        </AppButton>
        <button type="button" className="pg-linklike" onClick={enqueueKyc}>
          or save offline &amp; sync later
        </button>
      </div>
    );
  }

  // ── Client-side incomplete state (empty variant) ───────────────────────────
  if (missing.length > 0) {
    return (
      <>
        <SummaryRows lead={lead} photosText={photosText(lead, normalisePhotos(local?.photos))} decisionText={decisionText(local?.duplicateDecision?.decision)} />
        <div className="pg-inline-error" role="alert">
          <IconWarning size={14} />
          {missing.length} item{missing.length === 1 ? "" : "s"} incomplete — KYC cannot be
          submitted yet
        </div>
        <div className="pg-actions">
          <AppButton
            variant="secondary"
            type="button"
            onClick={() =>
              navigate(local?.formDraft ? `/field/leads/new?draft=${local.localId}` : "/field/leads/new")
            }
          >
            Return to Form
          </AppButton>
        </div>
      </>
    );
  }

  // ── Default: read-only summary + confirm ───────────────────────────────────
  return (
    <>
      <SummaryRows lead={lead} photosText={photosText(lead, normalisePhotos(local?.photos))} decisionText={decisionText(local?.duplicateDecision?.decision)} />
      <div className="pg-actions">
        <AppButton type="button" onClick={handleSubmit}>
          Confirm &amp; Submit KYC
        </AppButton>
      </div>
    </>
  );
}

function photosText(lead: Lead, photos: LeadPhotos | undefined): string {
  const one = (
    urlOnLead: string | null,
    meta: { name: string; state: string } | undefined,
    label: string,
  ): string | null => {
    if (urlOnLead || meta?.state === "uploaded") return `${label} ✓`;
    if (meta?.state === "pending" || meta?.state === "uploading") return `${label} pending sync`;
    if (meta?.state === "failed") return `${label} failed — recapture`;
    return null;
  };
  const customer = one(lead.photo_customer_url, photos?.customer, "Customer");
  const house = one(lead.photo_house_url, photos?.house, "House");
  if (customer && house) return `${customer} · ${house}`;
  if (customer) return `${customer} · Missing house photo`;
  if (house) return `Missing customer photo · ${house}`;
  return "Not captured";
}

function decisionText(decision: "merge" | "reject" | "continue" | undefined): string {
  if (decision === "continue") return "Continue as new";
  if (decision === "merge") return "Merge records";
  if (decision === "reject") return "Rejected — not a duplicate";
  return "—";
}

function SummaryRows({
  lead,
  photosText: photos,
  decisionText: decision,
}: {
  lead: Lead;
  photosText: string;
  decisionText: string;
}) {
  return (
    <div className="pg-summary">
      <div className="row">
        <span className="k">Name</span>
        <span className="v">{`${lead.first_name} ${lead.last_name}`.trim() || "—"}</span>
      </div>
      <div className="row">
        <span className="k">NIDA</span>
        <span className="v">{lead.national_id ?? "—"}</span>
      </div>
      <div className="row">
        <span className="k">Phone</span>
        <span className="v">{lead.phone || "—"}</span>
      </div>
      <div className="row">
        <span className="k">Ward</span>
        <span className="v">{lead.ward ?? "—"}</span>
      </div>
      <div className="row">
        <span className="k">GPS</span>
        <span className="v">{gpsSummary(lead)}</span>
      </div>
      <div className="row">
        <span className="k">Photos</span>
        <span className="v">{photos}</span>
      </div>
      <div className="row">
        <span className="k">Duplicate review</span>
        <span className="v">{decision}</span>
      </div>
    </div>
  );
}
